/* The visit's language, outside React (like lib/funnel/session.ts) so the first paint, the API
 * calls and the formatters all read the same value.
 *
 * - `chosen`: detected (detect.ts) or picked in the sheet. Sent to the API as the passenger's
 *   language, as chosen: the API stores it and answers through its levers.
 * - `active`: what the header shows and the sheet ticks: `chosen`, or English while the
 *   language's lever is off (`GET /languages`; unknown or unreachable = every one on).
 * - `text`: the language the copy is in: `active` once its dictionary is in, English when this
 *   build has no copy for it. `<html lang dir data-script>` and the formatters follow it.
 */
import { useSyncExternalStore } from 'react';
import type { LanguageInfo } from '../../lib/api/contract';
import { setFormatLocale } from '../../lib/format';
import { browserEnv, detectLocale, storedChoice, type DetectEnv, type DetectSource } from './detect';
import { loadLocale, type Dict } from './loader';
import { dirOf, type Locale } from './registry';

export interface LanguageState {
  chosen: Locale;
  active: Locale;
  text: Locale;
  /** The API's levers by code; null until read (or when it can't be). */
  levers: ReadonlyMap<Locale, LanguageInfo> | null;
  /** A dictionary is on its way. */
  loading: boolean;
}

const INITIAL: LanguageState = { chosen: 'en', active: 'en', text: 'en', levers: null, loading: false };
let state = INITIAL;
const listeners = new Set<() => void>();
const dicts = new Map<Locale, Dict | null>();

function set(patch: Partial<LanguageState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

export const languageStore = {
  get: () => state,
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

export const useLanguage = (): LanguageState =>
  useSyncExternalStore(languageStore.subscribe, languageStore.get, languageStore.get);

/** A loaded locale's dictionary (translate()). */
export const dictionaryOf = (l: Locale): Dict | null | undefined => dicts.get(l);

/** Off only when the API says so. English is always on. */
export function isEnabled(l: Locale, levers = state.levers): boolean {
  return l === 'en' || !levers || levers.get(l)?.enabled !== false;
}

/** The page follows the copy's language. */
function applyDocument(text: Locale, script: string) {
  const html = globalThis.document?.documentElement;
  if (html) {
    html.lang = text;
    html.dir = dirOf(text);
    html.dataset.script = script;
  }
  setFormatLocale(text);
}

let seq = 0;
/** Brings `active` and `text` in line with `chosen` and the levers. Never rejects. */
async function settle(): Promise<void> {
  const target = isEnabled(state.chosen) ? state.chosen : 'en';
  const n = ++seq;
  let script = 'Latn';
  if (target !== 'en') {
    set({ loading: true });
    try {
      const scripts = import('./scripts').then(async (m) => {
        await m.loadLocaleFont(target);
        return m;
      });
      const [dict, m] = await Promise.all([
        dicts.has(target) ? (dicts.get(target) ?? null) : loadLocale(target),
        scripts,
      ]);
      dicts.set(target, dict);
      if (dict) script = m.scriptOf(target);
    } catch {
      // A chunk didn't load (offline, or a deploy replaced it): stay as we are; a later pick tries again.
      if (n === seq) set({ loading: false });
      return;
    }
  }
  if (n !== seq) return;
  const text = target === 'en' || dicts.get(target) ? target : 'en';
  applyDocument(text, script);
  set({ active: target, text, loading: false });
}

/**
 * The first language of the visit (the app's boot). `?lang=` is remembered like a pick.
 * Resolves once its copy is in (or failed to come).
 */
export function startLanguage(env: DetectEnv = browserEnv()): { source: DetectSource; ready: Promise<void> } {
  const { locale, source } = detectLocale(env);
  if (source === 'query') storedChoice.set(locale);
  set({ chosen: locale });
  return { source, ready: settle() };
}

/** A pick in the sheet: remembered, then shown once its copy is in. */
export function chooseLanguage(l: Locale): Promise<void> {
  storedChoice.set(l);
  set({ chosen: l });
  return settle();
}

/** The API's levers (`GET /languages`); null when it couldn't be read: everything on. */
export function setLevers(list: readonly LanguageInfo[] | null): Promise<void> {
  set({ levers: list ? new Map(list.map((x) => [x.code, x] as const)) : null });
  return settle();
}

/** Tests: back to English, nothing loaded (or `dicts` given as already loaded). */
export function resetLanguage(preloaded: Partial<Record<Locale, Dict>> = {}) {
  seq++;
  dicts.clear();
  for (const [l, d] of Object.entries(preloaded)) dicts.set(l as Locale, d);
  state = INITIAL;
  applyDocument('en', 'Latn');
  listeners.forEach((l) => l());
}

/** Tests: a dictionary as if its chunk had loaded. */
export function addDictionary(l: Locale, d: Dict) {
  dicts.set(l, d);
}
